import type { FurniturePart, Vec2 } from "../types";
import { boundingBox } from "../geom";
import { outlinePath, type PathCmd } from "./path";
import { getMaterialTile, materialInfo, type MaterialTile } from "./materials";
import { paintFor, partMaterial, type PartPaint } from "./shading";
import type { ResolvedFurniture } from "./resolve";
import { shade } from "./color";

/**
 * Level of detail. Chosen from the piece's on-screen size so tiny pieces stay
 * legible (a 15" stool at 2 px/in is 30 px wide — seams would be noise).
 *   0 — footprint silhouette only
 *   1 — parts, no seams / textures / part shadows
 *   2 — everything
 */
export type Lod = 0 | 1 | 2;

export function lodForPx(minPx: number): Lod {
  if (minPx < 26) return 0;
  if (minPx < 64) return 1;
  return 2;
}

export interface PartDraw {
  part: FurniturePart;
  cmds: PathCmd[];
  paint: PartPaint;
  /** Material tile for pattern fills (null → flat fill). */
  tile: MaterialTile | null;
  /** Pattern rotation in degrees (grain direction). */
  patternRotation: number;
  /** Seams to stroke on top of the part. */
  seams: { a: Vec2; b: Vec2 }[];
}

export interface RenderPlan {
  /** Footprint outline path (inch space). */
  footprintCmds: PathCmd[];
  /** Flat color used for the footprint base / LOD-0 silhouette. */
  baseFill: string;
  parts: PartDraw[];
  lod: Lod;
}

const planCache = new WeakMap<ResolvedFurniture, Map<Lod, RenderPlan>>();

export function buildRenderPlan(r: ResolvedFurniture, lod: Lod, withTextures = true): RenderPlan {
  let byLod = planCache.get(r);
  if (!byLod) {
    byLod = new Map();
    planCache.set(r, byLod);
  }
  const key = (withTextures ? lod : ((lod + 10) as unknown)) as Lod;
  const hit = byLod.get(key);
  if (hit) return hit;

  const footprintCmds = outlinePath(r.footprint, footprintRadius(r));
  const baseFill = shade(r.material.tint, -0.04);
  const parts: PartDraw[] = [];
  if (lod >= 1) {
    for (const p of r.parts) {
      const paint = paintFor(p, r.material, r.maxZ);
      const mat = partMaterial(p, r.material);
      const tile =
        lod >= 2 && withTextures && paint.textureStrength > 0
          ? getMaterialTile(mat, paint.fill, paint.textureStrength)
          : null;
      const info = materialInfo(mat.id);
      let patternRotation = 0;
      if (info.directional) {
        const bb = boundingBox(p.outline);
        patternRotation = bb.height > bb.width ? 90 : 0;
      }
      parts.push({
        part: p,
        cmds: outlinePath(p.outline, p.cornerRadius ?? 0),
        paint,
        tile,
        patternRotation,
        seams: lod >= 2 ? (p.seams ?? []) : [],
      });
    }
  }
  const plan: RenderPlan = { footprintCmds, baseFill, parts, lod };
  byLod.set(key, plan);
  return plan;
}

/** Rectangular footprints get the body's corner radius so the silhouette
 *  matches the topmost body part; polygons stay sharp. */
function footprintRadius(r: ResolvedFurniture): number {
  if (r.footprint.length !== 4) return 0;
  const body = r.parts.find((p) => p.role === "body" && p.outline.length === 4);
  return body?.cornerRadius ?? 0;
}
